import uniqueId from 'lodash-es/uniqueId';
import React from 'react';
import { TTab, TTabsListState, TabsControllerProps } from './types';
import { getLabel, getSpecificParent, isChild } from '@apia/util';
import { ApiaUtil } from '../objects/ApiaUtil';
import { makeAutoObservable, observable } from 'mobx';

export class Tab<TabType extends TTab = TTab> {
  constructor(
    public state: TabType,
    public controller: TabsController<TabType>,
  ) {
    makeAutoObservable(this, { state: observable });
  }

  get id() {
    return this.state.id;
  }

  /**
   * El parámetro force evita que se muestre cartel de confirmación al usuario cuando puede haber pérdida de datos.
   */
  async close(force?: boolean) {
    return this.controller.closeTab(this.state.id, force);
  }

  open() {
    this.controller.openTab(this.id);
  }
}

export default class TabsController<TabType extends TTab = TTab> {
  state: TTabsListState<TabType> = {
    openTabs: new Set(),
    tabs: new Map(),
    timestamp: 0,
  };

  getActiveTab() {
    return this.activeTab;
  }
  /**
   * Es el tab que está abierto
   */
  get activeTab() {
    const tabs = [...this.state.openTabs];

    return tabs[tabs.length - 1];
  }

  getTab(id: string) {
    return this.state.tabs.get(id);
  }

  get tabsList() {
    return [...this.state.tabs.values()];
  }

  constructor(
    public id: string,
    public props: Omit<TabsControllerProps<TabType>, 'id'>,
  ) {
    props.initialTabs?.forEach(this.append.bind(this));
    if (props.initialTabs && props.initialTabs.length > 0)
      this.focusTab(props.initialTabs[0].id);

    makeAutoObservable(this);
  }

  #innerAppend(tab: TabType) {
    this.state.tabs.set(tab.id, new Tab(tab, this));

    if (tab.isOpen) {
      this.openTab(tab.id);
      this.focusTab(tab.id);
    }
  }

  /**
   * Obviamente agrega un tab al listado de tabs
   */
  append(tab: TabType) {
    if (this.getTab(tab.id)) {
      void ApiaUtil.instance.dialogs
        .confirm({
          children: getLabel('msgTabIsOpen').text,
          title: getLabel('titMsgWarning').text,
        })
        .then((hasConfirmed) => {
          if (hasConfirmed)
            this.#innerAppend({
              ...tab,
              id: `${tab.id}_${uniqueId()}`,
              additionalProps: tab.additionalProps ?? {},
            } as TabType);
        });
    } else this.#innerAppend(tab);
  }

  async closeAll(closeFixedTabsAsWell = false, force = false) {
    for (const tab of this.tabsList) {
      if (!tab.state.isFixed || closeFixedTabsAsWell) {
        tab.close(force);
      }
    }
  }

  closeOpenTab(force?: boolean) {
    const openTab = this.activeTab;
    openTab?.close(force);
  }

  async closeToRight(targetId: string, force?: boolean) {
    const tabsList = this.tabsList;
    const startIndex = tabsList.findIndex((c) => c.state.id === targetId);

    if (startIndex >= 0) {
      for (let i = startIndex + 1; i < tabsList.length; i++) {
        tabsList[i].close(force);
      }
    }
  }

  async closeToLeft(targetId: string, force?: boolean) {
    const tabsList = this.tabsList;
    const startIndex = tabsList.findIndex((c) => c.state.id === targetId);

    if (startIndex >= 0) {
      for (let i = startIndex - 1; i >= 0; i--) {
        await tabsList[i].close(force);
      }
    }
  }

  async closeOthers(targetId: string, force?: boolean) {
    for await (const tab of this.tabsList) {
      if (tab.state.id !== targetId) {
        await tab.close(force);
      }
    }
  }

  async closeTab(tabId: string, force = false) {
    const tab = this.getTab(tabId);
    const tabProps = tab?.state;

    if (tab && tabProps) {
      if (!tabProps.isClosable && !force) return false;

      const { onBeforeClose } = tabProps;
      if (onBeforeClose) {
        const canClose = await onBeforeClose();
        const message =
          canClose === false ? getLabel('msgPerDatIng').text : canClose;
        if (message !== true && !force) {
          const hasConfirmed = await ApiaUtil.instance.dialogs.confirm({
            children: message,
            title: getLabel('lbl_atention').text,
          });
          if (!hasConfirmed) return false;
        }
      }

      this.state.openTabs.delete(tab);
      this.getActiveTab()?.open();
      this.state.tabs.delete(tab.id);

      this.props?.onCloseTab?.(tab);
    }
    return true;
  }

  focusNextTab() {
    const tabsList = this.tabsList;
    const focusedIndex = tabsList.findIndex(
      (current) => current === this.activeTab,
    );

    if (focusedIndex === tabsList.length - 1 || focusedIndex === -1)
      return null;

    const newFocusIndex: number = focusedIndex + 1;
    this.focusTab(tabsList[newFocusIndex].id);

    return tabsList[newFocusIndex];
  }

  focusPreviousTab() {
    const tabsList = this.tabsList;
    const focusedIndex = tabsList.findIndex(
      (current) => current === this.activeTab,
    );

    if (focusedIndex <= 0) return null;

    const newFocusIndex: number = focusedIndex - 1;
    this.focusTab(tabsList[newFocusIndex].id);

    return tabsList[newFocusIndex];
  }

  focusTab(tabId: string) {
    this.openTab(tabId);
  }

  getTabElement(tabId: string) {
    return document.querySelector(`#tabpanel-${this.id}-${tabId}`);
  }

  handleKeyDown(ev: React.KeyboardEvent) {
    if (
      !isChild(
        ev.target as HTMLElement,
        (current) => current.id === `tabsList-${this.id}`,
      )
    )
      return;

    switch (ev.code) {
      case 'ArrowDown':
      case 'ArrowRight': {
        if (
          (this.props?.orientation === 'horizontal' &&
            ev.code === 'ArrowDown') ||
          (this.props?.orientation === 'vertical' && ev.code === 'ArrowRight')
        )
          return;

        this.focusNextTab();

        break;
      }
      case 'ArrowUp':
      case 'ArrowLeft': {
        if (
          (this.props.orientation === 'horizontal' && ev.code === 'ArrowUp') ||
          (this.props.orientation === 'vertical' && ev.code === 'ArrowLeft')
        )
          return;

        this.focusPreviousTab();

        break;
      }
      case 'Delete': {
        const tabId = getSpecificParent(
          ev.target as HTMLElement,
          (current) => !!current.getAttribute('data-id'),
        )?.getAttribute('data-id');

        if (tabId) {
          if (!this.getTab(tabId)?.state.isClosable) return;

          void this.closeTab(tabId);
        }
        break;
      }
      case 'Enter':
      case 'Space': {
        const tabId = getSpecificParent(
          ev.target as HTMLElement,
          (current) => !!current.getAttribute('data-id'),
        )?.getAttribute('data-id');

        if (tabId) {
          this.openTab(tabId);
        }

        break;
      }
      case 'End': {
        this.openTab(this.tabsList[this.tabsList.length - 1].id);
        break;
      }
      case 'Home': {
        this.focusTab(this.tabsList[0].id);
        break;
      }
      default: {
        break;
      }
    }
  }

  openNextTab() {
    const tabsList = this.tabsList;
    const openIndex = tabsList.findIndex(
      (current) => current === this.activeTab,
    );

    if (openIndex == tabsList.length - 1) return null;

    const newOpenIndex: number = openIndex + 1;
    this.openTab(tabsList[newOpenIndex].id);

    return tabsList[newOpenIndex];
  }

  openPreviousTab() {
    const tabsList = this.tabsList;
    const openIndex = tabsList.findIndex(
      (current) => current === this.activeTab,
    );

    if (openIndex <= 0) return null;

    const newOpenIndex: number = openIndex - 1;
    this.openTab(tabsList[newOpenIndex].id);

    return tabsList[newOpenIndex];
  }

  /**
   * Se utiliza para hacer que el tab aparezca visible en el listado
   */
  openTab(tabId: string) {
    const tab = this.getTab(tabId);
    if (!tab) {
      console.warn(`There is no tab with such id: ${tabId}`);
    } else {
      try {
        this.state.tabs.forEach((c) => {
          c.state.isOpen = false;
        });
      } catch (e) {
        e; // No hay ningún tab abierto, no me importa
      }
      tab.state.isOpen = true;
      tab.state.isFocused = true;
      document
        .querySelector<HTMLElement>(`.tabs__item__wrapper[data-id="${tab.id}"]`)
        ?.focus();
      tab.state.onFocus?.(tab);
      if (this.state.openTabs.has(tab)) {
        this.state.openTabs.delete(tab);
      }
      this.state.openTabs.add(tab);
    }
  }
}
